import { useEffect, useState } from 'react';
import { sendTestPush, subscribePush } from './api';
import { pushStateFor, readDevice, urlBase64ToUint8Array, type PushState } from './push';

type Message = { tone: 'good' | 'bad' | 'info'; text: string };

const EXPLANATION: Record<PushState, string> = {
  'needs-install':
    'On iPhone, notifications only work for an app opened from the Home Screen. In Safari tap Share → Add to Home Screen, then open Personal OS from its icon and come back to this page.',
  unsupported: "This browser can't receive notifications.",
  blocked:
    'Notifications are blocked. In iPhone Settings → Notifications → Personal OS, allow them. If Personal OS is not listed, delete the app from the Home Screen and add it again.',
  'permission-needed': 'Tap the button, then choose Allow.',
  enabled: 'Notifications are on for this device.',
};

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.';
}

export function NotificationsCard({
  token,
  vapidPublicKey,
}: {
  token: string;
  vapidPublicKey: string;
}) {
  const [device, setDevice] = useState(readDevice);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let cancelled = false;
    void (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (!cancelled) setHasSubscription(existing !== null);
      } catch {
        // No worker or no push support: the card already explains that.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const state = pushStateFor(device, hasSubscription);

  async function enable() {
    setBusy(true);
    setMessage(null);
    try {
      // Must be the first thing awaited, so iPhone still sees it as the result of your tap.
      const permission = await Notification.requestPermission();
      setDevice(readDevice());
      if (permission !== 'granted') {
        setMessage({ tone: 'bad', text: 'Notifications were not allowed.' });
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const subscription =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        }));
      await subscribePush(token, subscription.toJSON());
      setHasSubscription(true);
      setMessage({ tone: 'good', text: 'Done. This device can now receive notifications.' });
    } catch (err) {
      setMessage({ tone: 'bad', text: describeError(err) });
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      await (await reg.pushManager.getSubscription())?.unsubscribe();
      setHasSubscription(false);
      setMessage({
        tone: 'info',
        text: 'Unsubscribed. Tap "Enable notifications" to start again.',
      });
    } catch (err) {
      setMessage({ tone: 'bad', text: describeError(err) });
    } finally {
      setBusy(false);
    }
  }

  async function send(delaySeconds: 0 | 30) {
    setBusy(true);
    setMessage(null);
    try {
      const result = await sendTestPush(token, delaySeconds);
      if (delaySeconds === 30) {
        setMessage({
          tone: 'info',
          text: 'Scheduled. Now close this app and lock your phone. The notification should arrive in about 30 seconds. The list below shows what the push service answered.',
        });
      } else {
        const first = result.results?.[0];
        setMessage({
          tone: result.ok ? 'good' : 'bad',
          text: result.ok
            ? `Sent (the push service answered ${first?.status ?? 'OK'}). It should appear in a moment.`
            : `The push service refused it (${first?.status ?? 'no answer'}).`,
        });
      }
    } catch (err) {
      setMessage({ tone: 'bad', text: describeError(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-labelledby="notif-title">
      <h2 id="notif-title" className="card-heading">
        Notifications
      </h2>
      <p className="card-detail" data-testid="push-state" data-state={state}>
        {EXPLANATION[state]}
      </p>

      {state === 'permission-needed' && (
        <button type="button" className="primary" disabled={busy} onClick={() => void enable()}>
          Enable notifications
        </button>
      )}

      {state === 'enabled' && (
        <div className="button-row">
          <button type="button" className="primary" disabled={busy} onClick={() => void send(30)}>
            Send in 30 seconds
          </button>
          <button type="button" disabled={busy} onClick={() => void send(0)}>
            Send now
          </button>
          <button type="button" disabled={busy} onClick={() => void reset()}>
            Start over
          </button>
        </div>
      )}

      {message && (
        <p role="status" className={message.tone === 'bad' ? 'error-text' : 'status-text'}>
          {message.text}
        </p>
      )}
    </section>
  );
}
